//Library imports
import { Fragment, useState } from 'react';
import { useAsync } from 'react-async';
import { useLocation, useParams } from 'react-router-dom';

//Components
import Layout from '../../components/layout';
import DataTable from '../../components/dataTable';
import { billExportedById, downloadBills, downloadBillsDebug, sendBills } from '../../api/services/quickbooksService'
import { Notification } from '../../components/notifications';
import moment from 'moment';
import fileDownload from 'js-file-download';
import StringResponse from '../../api/responses/stringResponse';
import BillExportModel from '../../api/models/billExportModel';

//GET params
type Params = { id: string; };

//Get our view state
type ViewState = {
    message?: string;
};

/**
 * Methods to fetch search criteria with which to populate
 */
const fetchBillDetails = async ({ id }: any) => await billExportedById(id);

/**
 * Primary Component
 */
const BillExportDetails = () => {
    const { id } = useParams<Params>();
    const [debug, setDebug] = useState<BillExportModel[]>([]);

    //Process our DataTable columns and data
    const columns = () => [
        {
            Header: 'Therapist Name',
            accessor: (u: any) => {
                return u.name;
            },
        },
        {
            Header: 'Bill Number',
            accessor: (u: any) => {
                return moment(u.billNumber).format('M/DD/YYYY');
            },
        },
        {
            Header: 'Status',
            accessor: (u: any) => {
                return u.status;
            },
        },
    ];

    //App hooks
    const { state } = useLocation<ViewState>();
    const [isLoading, setIsLoading] = useState(false);

    //Data hooks
    const [detailData, setDetailData] = useState<[]>([])

    const billDetailsData = useAsync({
        promiseFn: fetchBillDetails, id,
        onResolve: (response: any) => {
            setDetailData(response.data);
        },
    });

    const doSendBill = async () => {
        setIsLoading(true);
        const req = {
            "billExportId": Number.parseInt(id)
        };
        await sendBills(req).finally(() => {
            setIsLoading(false);
        });
    }

    const handleSendBill = () => {
        doSendBill();
    };

    const downloadBillsCsv = () => {
        const billExportId = Number.parseInt(id);
        downloadBillCsvAsync.run(billExportId);
    }

    const downloadBillCsvAsync = useAsync({
        deferFn: (args: any[]) => downloadBills(args[0]),
        onResolve: (response: StringResponse) => {
            const now = moment(new Date()).format('yyyy-MM-DD_THHmm');
            fileDownload(response.data, `PTS_QuickBooks_Bills_${now}.iif`);
        },
        onReject: (error: Error) => {
            window.alert(error);
        },
    });

    const downloadBillsCsvDebug = () => {
        setDebug ([]);
        const billExportId = Number.parseInt(id);
        downloadBillCsvDebugAsync.run(billExportId);
    }

    const downloadBillCsvDebugAsync = useAsync({
        deferFn: (args: any[]) => downloadBillsDebug(args[0]),
        onResolve: (response) => {
            setDebug(response.data);
        },
        onReject: (error: Error) => {
            window.alert(error);
        },
    });

    return (
        <Layout isLoading={
            billDetailsData.isPending || isLoading
        }>
            <Fragment>
                <Fragment>
                    {state?.message && <Notification>{state?.message}</Notification>}

                    <div className="mb-5">
                        <button className='bg-ptsblue-normal hover:bg-ptsblue-dark' onClick={downloadBillsCsv}>
                            Download All Therapists
                        </button>
                        <button className='bg-black hover:bg-red-500 hidden' onClick={downloadBillsCsvDebug}>
                            Download All Therapists (Debug)
                        </button>
                        <button className='mx-2 bg-ptsgreen-normal hover:bg-ptsgreen-dark'
                            onClick={() => handleSendBill()}
                        >
                            Sync/Send Bills to QuickBooks
                        </button>
                        <button className='bg-gray-400 hover:bg-gray-500'
                            onClick={() => billDetailsData.reload()}
                        >
                            Refresh Status
                        </button>
                    </div>

                    {/* Results Table */}
                    {detailData !== null && detailData !== undefined && (
                        <div className='results-table'>
                            {detailData.length > 0 && (
                                <div className='shadow overflow-hidden border-b border-gray-200 rounded-lg'>
                                    <DataTable
                                        columns={columns()}
                                        data={detailData}
                                        showPagination={detailData.length > 30 ? true : false}
                                        skipFilter={true}
                                    />
                                </div>
                            )}
                        </div>
                    )}
                </Fragment>
                {debug.length > 0 && <ul>
                    {debug.map(x => <li>{x.therapistName} - {x.schoolName} - {x.minutes} Minutes - Total: {x.totalAmount}</li>)}
                </ul>}
            </Fragment>
        </Layout>
    );
};
export default BillExportDetails;